/**
 * Minimal SVG sanitizer for operator-authored icons stored in datastorage.
 *
 * Strips:
 *  - <script> tags
 *  - event handler attributes (on*)
 *  - external references (xlink:href / href pointing to javascript: or
 *    absolute external URLs)
 *
 * Returns the sanitized SVG string, or an empty string if parsing fails.
 * This is intentionally lightweight — these icons are operator-authored
 * (not user-generated) and stored server-side, so we only need a defense
 * in depth before injecting via dangerouslySetInnerHTML.
 */
/**
 * Tints a sanitized SVG string so its strokes/fills inherit `currentColor`.
 *
 * Operator-authored gestion icons are typically Lucide-style outline icons
 * that use `fill="none"` on the root and a hard-coded `stroke` color. When
 * hubs inject these via dangerouslySetInnerHTML inside a colored container,
 * the inner shapes ignore the container's `color` and stay their original
 * color. This post-processor rewrites the SVG so strokes (and any black fills)
 * use `currentColor`, letting the surrounding `color`/theme drive the icon tint
 * WITHOUT filling outline icons solid.
 *
 * Rules:
 *  - Root <svg>: convert any stroke color to `currentColor`; keep `fill="none"`
 *    so child shapes stay unfilled; drop `fill` only if it's a solid color
 *    (not `none`); drop fixed `color` attr.
 *  - Shape elements (path, circle, rect, polygon, ellipse, line, g, ...):
 *      * `fill="#000"` / `fill="black"` / `fill="rgb(0,0,0)"` -> `currentColor`
 *      * missing `fill` -> left untouched (inherits from parent, typically
 *        `fill="none"` for outline icons)
 *      * `fill="none"` and any other explicit color are kept untouched.
 *      * any `stroke` with a color -> `currentColor` (so outline icons pick
 *        up the theme color regardless of the original stroke color)
 *      * missing `stroke` is left untouched.
 *
 * Operates on the already-sanitized SVG string with regex/string ops (no heavy
 * DOM parser dependency). Returns the tinted SVG string.
 */
export function tintSvgCurrentColor(svgString) {
  if (typeof svgString !== 'string' || svgString.length === 0) {
    return '';
  }

  let out = svgString;

  // On the root <svg ...>: convert stroke color to currentColor, keep
  // fill="none", only drop solid (non-none) fills, drop fixed color attr.
  out = out.replace(/<svg\b([^>]*)>/i, (match, attrs) => {
    let cleaned = attrs;

    // stroke -> currentColor (any color, not just black)
    cleaned = cleaned.replace(/\bstroke\s*=\s*"([^"]*)"/gi, (m, val) => {
      const v = val.trim().toLowerCase();
      return v === 'none' ? m : 'stroke="currentColor"';
    });
    cleaned = cleaned.replace(/\bstroke\s*=\s*'([^']*)'/gi, (m, val) => {
      const v = val.trim().toLowerCase();
      return v === 'none' ? m : 'stroke="currentColor"';
    });

    // fill: keep "none", drop solid colors so inheritance flows, drop black
    // (we don't rewrite to currentColor here because child shapes handle that).
    cleaned = cleaned.replace(/\sfill\s*=\s*"([^"]*)"/gi, (m, val) => {
      const v = val.trim().toLowerCase();
      return v === 'none' ? m : '';
    });
    cleaned = cleaned.replace(/\sfill\s*=\s*'([^']*)'/gi, (m, val) => {
      const v = val.trim().toLowerCase();
      return v === 'none' ? m : '';
    });

    // Drop fixed color attr so currentColor inheritance works.
    cleaned = cleaned
      .replace(/\scolor\s*=\s*"[^"]*"/gi, '')
      .replace(/\scolor\s*=\s*'[^']*'/gi, '');

    return `<svg${cleaned}>`;
  });

  // Shape elements that may carry fill/stroke.
  const shapeTagPattern =
    /\b(path|circle|rect|polygon|ellipse|line|polyline|g|use|symbol)\b/gi;

  // Process each opening tag of a shape element.
  out = out.replace(
    /<([a-zA-Z]+)\b([^>]*?)(\/?)>/g,
    (match, tag, attrs, selfClose) => {
      shapeTagPattern.lastIndex = 0;
      if (!shapeTagPattern.test(tag)) {
        return match;
      }

      let newAttrs = attrs;

      // --- fill ---
      const fillMatch = newAttrs.match(/\bfill\s*=\s*"([^"]*)"/i);
      if (fillMatch) {
        const val = fillMatch[1].trim().toLowerCase();
        if (val === '#000' || val === '#000000' || val === 'black' || /^rgb\(\s*0\s*,\s*0\s*,\s*0\s*\)$/.test(val)) {
          newAttrs = newAttrs.replace(fillMatch[0], 'fill="currentColor"');
        }
        // else: keep explicit color (including "none").
      }
      // NOTE: missing fill is intentionally left untouched so outline icons
      // inherit fill="none" from the root instead of being filled solid.

      // --- stroke ---
      const strokeMatch = newAttrs.match(/\bstroke\s*=\s*"([^"]*)"/i);
      if (strokeMatch) {
        const val = strokeMatch[1].trim().toLowerCase();
        if (val !== 'none') {
          // Any stroke color (not just black) -> currentColor so outline
          // icons pick up the theme color.
          newAttrs = newAttrs.replace(strokeMatch[0], 'stroke="currentColor"');
        }
        // else: keep stroke="none".
      }
      // NOTE: missing stroke is intentionally left untouched — the SVG default
      // stroke is "none", so adding currentColor would unexpectedly stroke
      // shapes that were designed to be unstroked.

      return `<${tag}${newAttrs}${selfClose}>`;
    }
  );

  return out;
}

export function sanitizeSvg(svgString) {
  if (typeof svgString !== 'string' || svgString.length === 0) {
    return '';
  }

  let doc;
  try {
    if (typeof DOMParser === 'undefined') {
      return '';
    }
    doc = new DOMParser().parseFromString(svgString, 'image/svg+xml');
  } catch (err) {
    return '';
  }

  if (!doc) {
    return '';
  }

  // DOMParser surfaces parse errors as a <parsererror> element.
  const parserError = doc.querySelector('parsererror');
  if (parserError) {
    return '';
  }

  const root = doc.documentElement;
  if (!root || root.nodeName.toLowerCase() !== 'svg') {
    return '';
  }

  // Remove <script> elements entirely.
  const scripts = doc.getElementsByTagName('script');
  while (scripts.length > 0) {
    const node = scripts[0];
    if (node.parentNode) {
      node.parentNode.removeChild(node);
    }
  }

  // Walk every element and strip dangerous attributes.
  const elements = doc.getElementsByTagName('*');
  for (let i = 0; i < elements.length; i += 1) {
    const el = elements[i];
    const attrs = Array.from(el.attributes);
    for (const attr of attrs) {
      const name = attr.name.toLowerCase();
      const value = String(attr.value || '').trim();

      // Event handlers (onclick, onload, onerror, ...).
      if (name.startsWith('on')) {
        el.removeAttribute(attr.name);
        continue;
      }

      // href / xlink:href with javascript: URIs or absolute external refs.
      if (name === 'href' || name === 'xlink:href') {
        if (/^\s*javascript:/i.test(value)) {
          el.removeAttribute(attr.name);
          continue;
        }
        // Block absolute external URLs (keep relative/data: inline refs only
        // for safety — operator icons should be self-contained).
        if (/^(https?:)?\/\//i.test(value)) {
          el.removeAttribute(attr.name);
        }
      }
    }
  }

  try {
    const xml = new XMLSerializer().serializeToString(root);
    return xml;
  } catch (err) {
    return '';
  }
}

export default sanitizeSvg;
